import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { ShopifyCart, Cart, CalculationResult, RedeemableAddPayload } from './types';
import { DiscountCodeInput } from './components/DiscountCodeInput';
import { DiscountSummary } from './components/DiscountSummary';
import { CartItems } from './components/CartItems';
import { StickyCheckout } from './components/StickyCheckout';
import { RedeemableProducts } from './components/RedeemableProducts';
import { transformToAPICart } from './utils/cartMapper';
import { fetchShopifyCart, calculateDiscounts, fetchCustomerInfo, createCheckout, type LoyaltyInfo } from './utils/apiClient';
import { getCustomerId } from './utils/customerData';
import {
  updateUrlWithVoucherCodes,
  mergeVoucherCodes,
  saveVoucherCodesToStorage,
} from './utils/voucherCodes';
import { updateCartUI } from './utils/cartUpdater';
import { LoyaltyStatus } from './components/LoyaltyStatus';
import { triggerConfetti } from './utils/confetti';
import { PhoneVerificationModal } from './components/PhoneVerificationModal';

const SKELETON_STYLE_ID = 'intan-skeleton-styles';

const ensureSkeletonStyles = () => {
  if (typeof document === 'undefined') {
    return;
  }

  if (document.getElementById(SKELETON_STYLE_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = SKELETON_STYLE_ID;
  style.innerHTML = `
    @keyframes intanShimmer {
      0% {
        transform: translateX(-100%);
      }
      100% {
        transform: translateX(100%);
      }
    }

    .intan-skeleton-block {
      position: relative;
      overflow: hidden;
      background-color: #e5e7eb;
    }

    .intan-skeleton-block::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      transform: translateX(-100%);
      background: linear-gradient(
        120deg,
        rgba(229, 231, 235, 0) 30%,
        rgba(255, 255, 255, 0.8) 50%,
        rgba(229, 231, 235, 0) 70%
      );
      animation: intanShimmer 1.6s infinite;
    }
  `;
  document.head.appendChild(style);
};

const SkeletonBlock: React.FC<{
  height: number | string;
  width?: number | string;
  borderRadius?: number | string;
  style?: React.CSSProperties;
}> = ({ height, width = '100%', borderRadius = '6px', style }) => (
  <div
    className="intan-skeleton-block"
    style={{
      height,
      width,
      borderRadius,
      ...style,
    }}
  />
);

const LoadingCartSkeleton: React.FC<{ cards?: number }> = ({ cards = 3 }) => {
  useEffect(() => {
    ensureSkeletonStyles();
  }, []);

  return (
    <div
      className="intan-discount-calculator"
      style={{ marginTop: '16px', position: 'relative' }}
    >
      <div className="intan-cart-items" style={{ marginBottom: '20px' }}>
        <SkeletonBlock height={18} width="160px" borderRadius="8px" />

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
          {Array.from({ length: cards }).map((_, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                gap: '12px',
                padding: '16px',
                backgroundColor: '#f5f5f5',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                position: 'relative',
              }}
            >
              <SkeletonBlock height={80} width={80} borderRadius="6px" />

              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <SkeletonBlock height={16} width="60%" />
                    <SkeletonBlock height={14} width="40%" style={{ marginTop: '8px' }} />
                    <SkeletonBlock height={12} width="50%" style={{ marginTop: '12px' }} />
                  </div>
                  <SkeletonBlock height={28} width={28} borderRadius="50%" />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <SkeletonBlock height={22} width={22} borderRadius="50%" />
                    <SkeletonBlock height={22} width={48} borderRadius="16px" />
                    <SkeletonBlock height={22} width={22} borderRadius="50%" />
                  </div>
                  <div style={{ textAlign: 'right', minWidth: '140px' }}>
                    <SkeletonBlock height={13} width="60%" />
                    <SkeletonBlock height={20} width="80%" style={{ marginTop: '10px' }} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

interface AppProps {
  apiUrl: string;
  liveSessionId?: string;
}

export const App: React.FC<AppProps> = ({ apiUrl, liveSessionId }) => {
  const [shopifyCart, setShopifyCart] = useState<ShopifyCart | null>(null);
  const [calculationResult, setCalculationResult] = useState<CalculationResult | null>(null);
  const [discountCodes, setDiscountCodes] = useState<string[]>([]);
  const [customerId, setCustomerId] = useState<string | undefined>();
  const [customerTags, setCustomerTags] = useState<string[]>([]);
  const [loyaltyInfo, setLoyaltyInfo] = useState<LoyaltyInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingQuantities, setUpdatingQuantities] = useState<Set<number>>(new Set());
  const [removingItems, setRemovingItems] = useState<Set<number>>(new Set());
  const [tierCelebrationActive, setTierCelebrationActive] = useState(false);
  const [creatingCheckout, setCreatingCheckout] = useState(false);
  const [, setHasPhone] = useState<boolean>(true); // Default to true to avoid showing modal unnecessarily
  const [showPhoneModal, setShowPhoneModal] = useState(false);

  const withLiveSession = useCallback(
    (cart: Cart): Cart =>
      liveSessionId ? { ...cart, live_session_id: liveSessionId } : cart,
    [liveSessionId]
  );

  // Initialize discount codes from URL and storage
  useEffect(() => {
    const mergedCodes = mergeVoucherCodes();
    setDiscountCodes(mergedCodes);
    if (mergedCodes.length > 0) {
      updateUrlWithVoucherCodes(mergedCodes);
    }
  }, []);

  // Get customer ID on mount
  useEffect(() => {
    const cid = getCustomerId();
    setCustomerId(cid);

    console.log('Detected customer ID:', cid);

    // Fetch customer info if customer ID exists
    if (cid) {
      fetchCustomerInfo(apiUrl, cid)
        .then((info) => {
          console.log('Fetched customer info:', info);
          setCustomerTags(info.tags || []);
          setLoyaltyInfo(info.loyalty || null);
          setHasPhone(info.hasPhone ?? true);

          // Show phone verification modal if customer is logged in but has no phone
          if (!info.hasPhone) {
            setShowPhoneModal(true);
          }
        })
        .catch((err) => {
          console.error('Failed to fetch customer info:', err);
        });
    }
  }, [apiUrl]);

  const {
    tierLabel,
    nextTierLabel,
    pointsToNextTier,
    currentPoints,
    effectiveMultiplier,
    projectedPointsEarned,
  } = useMemo(() => {
    const defaultTier = 'White';
    const tierRaw = loyaltyInfo?.tier ?? defaultTier;
    const normalizedTier = tierRaw.trim().toLowerCase() || defaultTier.toLowerCase();

    const tierConfig = {
      white: {
        label: 'White',
        nextTier: 'Maroon',
        nextThreshold: 1000,
        multiplierLow: 0.5,
        multiplierHigh: 1,
      },
      maroon: {
        label: 'Maroon',
        nextTier: 'Black',
        nextThreshold: 3000,
        multiplierLow: 0.75,
        multiplierHigh: 2,
      },
      black: {
        label: 'Black',
        nextTier: null,
        nextThreshold: null,
        multiplierLow: 1.5,
        multiplierHigh: 3,
      },
    } as const;

    const config =
      tierConfig[normalizedTier as keyof typeof tierConfig] ??
      tierConfig.white;

    const points = loyaltyInfo?.points ?? 0;
    const cartTotal = calculationResult
      ? calculationResult.final_total
      : shopifyCart
        ? shopifyCart.total_price / 100
        : 0;

    const multiplier =
      cartTotal >= 300 ? config.multiplierHigh : config.multiplierLow;
    const pointsEarned = cartTotal * multiplier;
    const pointsNeeded =
      config.nextThreshold !== null
        ? Math.max(config.nextThreshold - points, 0)
        : null;

    return {
      tierLabel: config.label,
      nextTierLabel: config.nextTier,
      pointsToNextTier: pointsNeeded,
      currentPoints: points,
      effectiveMultiplier: multiplier,
      projectedPointsEarned: pointsEarned,
    };
  }, [calculationResult, loyaltyInfo, shopifyCart]);

  const hasRedeemableProducts = useMemo(() => {
    if (!calculationResult?.potential_discounts) {
      return false;
    }

    return calculationResult.potential_discounts.some(
      (potentialDiscount) =>
        potentialDiscount.redeemableGroups &&
        potentialDiscount.redeemableGroups.length > 0
    );
  }, [calculationResult]);

  const isGuest = !loyaltyInfo;
  const confettiTriggeredRef = useRef(false);

  const syncCartState = useCallback(async (
    cart: ShopifyCart,
    options: {
      precomputedResult?: CalculationResult | null;
    } = {}
  ) => {
    const { precomputedResult = null } = options;
    let currentCart = cart;
    let result: CalculationResult | null = precomputedResult;

    setError(null);
    setCalculating(true);

    try {
      setShopifyCart(currentCart);

      if (!result) {
        const testCart: Cart = transformToAPICart(
          currentCart,
          customerId,
          customerTags,
          discountCodes, // Send to voucher_codes
          discountCodes, // Send to bogo_codes
          discountCodes, // Send to quantity_codes
          discountCodes  // Send to product_codes
        );

        result = await calculateDiscounts(apiUrl, withLiveSession(testCart));
      }

      if (result) {
        setCalculationResult(result);

        updateCartUI(currentCart.items, result);

        // console.log('Intan: Discount calculation complete', {
        //   cartItems: currentCart.items.length,
        //   totalDiscount: result.total_discount,
        //   appliedDiscounts: result.applied_discounts.length,
        // });
      } else {
        setCalculationResult(null);
      }

    } catch (err: any) {
      console.error('Error calculating discounts:', err);
      setError(err.message || 'Failed to calculate discounts');
    } finally {
      setCalculating(false);
    }
  }, [apiUrl, customerId, customerTags, discountCodes, withLiveSession]);

  // Fetch cart and calculate discounts
  const fetchAndCalculate = useCallback(async () => {
    try {
      setError(null);

      const shopifyCart = await fetchShopifyCart();
      setShopifyCart(shopifyCart);

      const cart: Cart = transformToAPICart(
        shopifyCart,
        customerId,
        customerTags,
        discountCodes, // Send to voucher_codes
        discountCodes, // Send to bogo_codes
        discountCodes, // Send to quantity_codes
        discountCodes  // Send to product_codes
      );

      const result = await calculateDiscounts(apiUrl, withLiveSession(cart));

      const updates = new Map<string, number>();
      const variantToKey = new Map<number | string, string>();
      const autoAddedVariantIds = new Set<number | string>();

      cart.items.forEach((item) => {
        const key = item.key;
        variantToKey.set(item.variant_id || '', key);
        updates.set(key, item.quantity);
      });

      if (result.updateCart) {
        Object.entries(result.updateCart).forEach(([rawKey, instruction]) => {
          let targetKey = rawKey;

          if (!updates.has(targetKey)) {
            const numericKey = Number(rawKey);
            if (!Number.isNaN(numericKey)) {
              const existingKey = variantToKey.get(numericKey);
              targetKey = existingKey || String(numericKey);
            }
          }

          updates.set(targetKey, instruction.quantity);

          if (instruction.auto_added) {
            const numericKey = Number(rawKey);
            if (!Number.isNaN(numericKey)) {
              autoAddedVariantIds.add(numericKey);
            } else {
              const variantMatch = Array.from(variantToKey.entries()).find(([, key]) => key === rawKey);
              if (variantMatch) {
                autoAddedVariantIds.add(variantMatch[0]);
              }
            }
          }
        });
      }

      const formData = new FormData();
      updates.forEach((quantity, key) => {
        formData.append(`updates[${key}]`, String(quantity));
      });

      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        body: formData,
      });

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to update cart (${updateResponse.status})`
        );
      }

      const updatedCart = await updateResponse.json();

      const autoAddedKeys = new Set<string>();
      if (result.updateCart) {
        updatedCart.items.forEach((item: any) => {
          const key = item.key || String(item.id);
          const entryByKey = result.updateCart![key];
          const entryByVariant = result.updateCart![String(item.variant_id)];
          if ((entryByKey?.auto_added || entryByVariant?.auto_added) || autoAddedVariantIds.has(item.variant_id)) {
            autoAddedKeys.add(key);
          }
        });
      }

      for (const key of autoAddedKeys) {
        const propFormData = new FormData();
        propFormData.append('id', key);
        propFormData.append('properties[_auto_added]', 'true');
        fetch('/cart/change.js', {
          method: 'POST',
          body: propFormData,
        }).catch((err) => {
          console.error('Failed to update item properties for key:', key, err);
        });
      }
      await syncCartState(updatedCart, {
        // precomputedResult: result, // todo: optimize with precomputedResult (need to check for temp id and failures)
      });
    } catch (err: any) {
      console.error('Error calculating discounts:', err);
      setError(err.message || 'Failed to calculate discounts');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, customerId, customerTags, discountCodes, syncCartState, withLiveSession]);

  const handleRedeemableAdd = useCallback(async (payload: RedeemableAddPayload) => {
    if (!shopifyCart) {
      throw new Error('Cart is not ready yet');
    }

    setError(null);
    setCalculating(true);

    try {
      const futureItems = shopifyCart.items.map((item) => ({ ...item }));
      payload.items.forEach((item) => {
        const existing = futureItems.find((cartItem) => cartItem.variant_id === item.variantId);
        if (existing) {
          existing.quantity += item.quantity;
        } else {
          const tempId = -Math.abs(item.variantId || Date.now());
          futureItems.push({
            id: tempId,
            variant_id: item.variantId,
            product_id: item.productId,
            quantity: item.quantity,
            price: item.price,
            title: item.title || `Variant ${item.variantId}`,
            properties: item.properties || {},
          });
        }
      });

      const futureCart: ShopifyCart = {
        ...shopifyCart,
        items: futureItems,
      };

      const cartPayload: Cart = transformToAPICart(
        futureCart,
        customerId,
        customerTags,
        discountCodes, // Send to voucher_codes
        discountCodes, // Send to bogo_codes
        discountCodes, // Send to quantity_codes
        discountCodes  // Send to product_codes
      );

      const result = await calculateDiscounts(apiUrl, withLiveSession(cartPayload));

      const updates = new Map<string, number>();
      const variantToKey = new Map<number, string>();
      const autoAddedVariantIds = new Set<number>();

      shopifyCart.items.forEach((item) => {
        const key = item.key || String(item.id);
        variantToKey.set(item.variant_id, key);
        updates.set(key, item.quantity);
      });

      payload.items.forEach((item) => {
        const key = variantToKey.get(item.variantId) || String(item.variantId);
        const currentQuantity = updates.get(key) || 0;
        updates.set(key, currentQuantity + item.quantity);
      });

      if (result.updateCart) {
        Object.entries(result.updateCart).forEach(([rawKey, instruction]) => {
          let targetKey = rawKey;

          if (!updates.has(targetKey)) {
            const numericKey = Number(rawKey);
            if (!Number.isNaN(numericKey)) {
              const existingKey = variantToKey.get(numericKey);
              targetKey = existingKey || String(numericKey);
            }
          }

          updates.set(targetKey, instruction.quantity);

          if (instruction.auto_added) {
            const numericKey = Number(rawKey);
            if (!Number.isNaN(numericKey)) {
              autoAddedVariantIds.add(numericKey);
            } else {
              const variantMatch = Array.from(variantToKey.entries()).find(([, key]) => key === rawKey);
              if (variantMatch) {
                autoAddedVariantIds.add(variantMatch[0]);
              }
            }
          }
        });
      }

      const formData = new FormData();
      updates.forEach((quantity, key) => {
        formData.append(`updates[${key}]`, String(quantity));
      });

      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        body: formData,
      });

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to update cart (${updateResponse.status})`
        );
      }

      const updatedCart = await updateResponse.json();

      const autoAddedKeys = new Set<string>();
      if (result.updateCart) {
        updatedCart.items.forEach((item: any) => {
          const key = item.key || String(item.id);
          const entryByKey = result.updateCart![key];
          const entryByVariant = result.updateCart![String(item.variant_id)];
          if ((entryByKey?.auto_added || entryByVariant?.auto_added) || autoAddedVariantIds.has(item.variant_id)) {
            autoAddedKeys.add(key);
          }
        });
      }

      for (const key of autoAddedKeys) {
        const propFormData = new FormData();
        propFormData.append('id', key);
        propFormData.append('properties[_auto_added]', 'true');
        fetch('/cart/change.js', {
          method: 'POST',
          body: propFormData,
        }).catch((err) => {
          console.error('Failed to update item properties for key:', key, err);
        });
      }

      await syncCartState(updatedCart,
        // { precomputedResult: result }
      );// todo: optimize with precomputedResult (need to check for temp id and failures)

      return true;
    } catch (err: any) {
      console.error('Error processing redeemable add:', err);
      setError(err?.message || 'Failed to add products to cart');
      setCalculating(false);
      throw err;
    }
  }, [shopifyCart, customerId, customerTags, discountCodes, apiUrl, syncCartState, withLiveSession]);

  const handleCartMutation = useCallback(async (itemId: number, quantity: number) => {
    // call calculate first
    // update current cart state to match the expected state after mutation

    const futureCart = {
      ...shopifyCart!,
      items: shopifyCart!.items.map(item => {
        if (item.id === itemId) {
          return { ...item, quantity };
        }
        return item;
      }).filter(item => item.id !== itemId || quantity > 0),
    }
    const cart: Cart = transformToAPICart(
      futureCart,
      customerId,
      customerTags,
      discountCodes,
      [],
      []
    );

    const result = await calculateDiscounts(apiUrl, withLiveSession(cart));

    let cartToUpdate: { [key:string]: {
      quantity: number;
      auto_added: boolean;
      }} = {
      [String(itemId)]: {
        quantity,
        auto_added: false,
      }
    };
    if (result.updateCart) {
      Object.entries(result.updateCart).forEach(([key, item]) => {
        if (cartToUpdate[key]) {
          if (cartToUpdate[key].quantity === item.quantity) {
            return;
          } else {
            cartToUpdate[key] = {
              quantity: item.quantity,
              auto_added: item.auto_added,
            }
          }
        }
        cartToUpdate[key] = item;
      });
    }


    const formData = new FormData();
    Object.entries(cartToUpdate).forEach(([key, item]) => {
      formData.append(`updates[${key}]`, String(item.quantity));
    });

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to update cart');
    }

    const updatedCart = await response.json() as ShopifyCart;

    const changeItem: { [key: string]: number} = {};

    // if the item was auto-added, update the line item to add _auto_added property
    updatedCart.items.forEach((item) => {
      const key = item.key;
      if (!key) {
        return;
      }

      const variantKey = item.variant_id != null ? String(item.variant_id) : undefined;
      const wasAutoAdded =
        !!cartToUpdate[key]?.auto_added ||
        (variantKey ? !!cartToUpdate[variantKey]?.auto_added : false);

      if (wasAutoAdded) {
        changeItem[key] = item.quantity;
      }
    });

    // change the properties of the auto-added items using /cart/change.js endpoint
    for (const key in changeItem) {
      const propFormData = new FormData();
      propFormData.append('id', key);
      propFormData.append('properties[_auto_added]', true as any);
      propFormData.append('quantity', String(changeItem[key]));
      fetch('/cart/change.js', {
        method: 'POST',
        body: propFormData,
      }).then((res) => {
        if (!res.ok) {
          console.error('Failed to update item properties for key:', key);
        }
        return res.json();
      })
    }

    if (Object.values(cartToUpdate).length > 0) { // Sync cart state with updated cart
      await syncCartState(updatedCart);
    } else {
      await syncCartState(updatedCart, { // no need to recalculate
        precomputedResult: result,
      });
    }
  }, [syncCartState, shopifyCart, customerId, customerTags, apiUrl, withLiveSession]);

  // Initial fetch
  useEffect(() => {
    fetchAndCalculate();
  }, [fetchAndCalculate]);

  // Handle discount codes change
  const handleDiscountCodesChange = useCallback((codes: string[]) => {
    setDiscountCodes(codes);
    updateUrlWithVoucherCodes(codes); // Keep using voucher URL param for backward compatibility
    saveVoucherCodesToStorage(codes);

    // Recalculate discounts with new codes
    // Send codes to all discount type arrays - backend will validate and match them
    if (shopifyCart) {
      const cart: Cart = transformToAPICart(
        shopifyCart,
        customerId,
        customerTags,
        codes, // Send to voucher_codes
        codes, // Send to bogo_codes
        codes, // Send to quantity_codes
        codes  // Send to product_codes
      );

      setCalculating(true);
      calculateDiscounts(apiUrl, withLiveSession(cart))
        .then((result) => {
          setCalculationResult(result);
          updateCartUI(shopifyCart.items, result);
        })
        .catch((err) => {
          console.error('Error recalculating discounts:', err);
          setError(err.message || 'Failed to recalculate discounts');
        })
        .finally(() => {
          setCalculating(false);
        });
    }
  }, [apiUrl, shopifyCart, customerId, customerTags, withLiveSession]);

  const handleCheckout = useCallback(async () => {
    if (!shopifyCart) {
      window.location.href = '/checkout';
      return;
    }

    setError(null);
    setCreatingCheckout(true);

    try {
      const cartPayload: Cart = transformToAPICart(
        shopifyCart,
        customerId,
        customerTags,
        discountCodes,
        [],
        []
      );

      const result = await createCheckout(apiUrl, {
        cart: withLiveSession(cartPayload),
        customer: customerId ? { id: customerId } : undefined,
        ...(liveSessionId ? { live_session_id: liveSessionId } : {}),
      });

      if (result.calculation) {
        setCalculationResult(result.calculation);
        updateCartUI(shopifyCart.items, result.calculation);
      }

      const targetUrl =
        result.draftOrder.checkout_url ||
        result.draftOrder.invoice_url ||
        '/checkout';

      window.location.href = targetUrl;
    } catch (err: any) {
      console.error('Error creating checkout:', err);
      setError(err?.message || 'Failed to create checkout');
      setTimeout(() => setError(null), 5000);
    } finally {
      setCreatingCheckout(false);
    }
  }, [shopifyCart, apiUrl, customerId, customerTags, discountCodes, withLiveSession, liveSessionId]);

  useEffect(() => {

    console.log('[Intan] Checking loyalty tier celebration conditions', {
      isGuest,
      nextTierLabel,
      pointsToNextTier,
    })

    if (typeof window === 'undefined') {
      return;
    }
    const nextTierExists = nextTierLabel !== null;
    const hasProjectedPoints =
      typeof projectedPointsEarned === 'number' &&
      Number.isFinite(projectedPointsEarned);

    const anticipatedPointsToNext =
      nextTierExists && typeof pointsToNextTier === 'number'
        ? pointsToNextTier - (hasProjectedPoints ? projectedPointsEarned : 0)
        : null;

    const shouldCelebrate =
      !isGuest &&
      nextTierExists &&
      anticipatedPointsToNext !== null &&
      anticipatedPointsToNext <= 0;

    if (shouldCelebrate && !confettiTriggeredRef.current) {
      console.log('[Intan] Triggering loyalty tier celebration confetti');
      triggerConfetti();
      confettiTriggeredRef.current = true;
      setTierCelebrationActive(true);
    } else if (!shouldCelebrate) {
      confettiTriggeredRef.current = false;
      setTierCelebrationActive(false);
    }
  }, [isGuest, nextTierLabel, pointsToNextTier, projectedPointsEarned]);

  if (loading) {
    return (
      <LoadingCartSkeleton cards={3} />
    );
  }

  return (
    <div className="intan-discount-calculator" style={{ marginTop: '16px', position: 'relative' }}>
      {!isGuest && (
        <LoyaltyStatus
          tier={tierLabel}
          currentPoints={currentPoints}
          nextTierLabel={nextTierLabel}
          pointsToNext={pointsToNextTier}
          cartTotal={calculationResult ? calculationResult.final_total : shopifyCart ? shopifyCart.total_price / 100 : 0}
          pointsEarned={projectedPointsEarned}
          effectiveMultiplier={effectiveMultiplier}
        isGuest={false}
        celebrating={tierCelebrationActive}
        />
      )}

      {/* Display cart items */}
      <CartItems
        shopifyCart={shopifyCart}
        calculationResult={calculationResult}
        updatingQuantities={updatingQuantities}
        removingItems={removingItems}
        onQuantityChange={(itemId, newQuantity, _delta) => {
          if (!shopifyCart) return;

          // Optimistic UI update - update immediately
          const previousCart = {
            ...shopifyCart,
            items: shopifyCart.items.map((item) => ({ ...item })),
          };

          const updatedItems = shopifyCart.items.map(item => {
            if (item.id === itemId) {
              return { ...item, quantity: newQuantity };
            }
            return item;
          });

          const updatedTotalPrice = updatedItems.reduce((sum, item) => {
            return sum + (item.price * item.quantity);
          }, 0);
          const updatedItemCount = updatedItems.reduce((sum, item) => sum + item.quantity, 0);

          setShopifyCart({
            ...shopifyCart,
            items: updatedItems,
            total_price: updatedTotalPrice,
            item_count: updatedItemCount,
          });

          setUpdatingQuantities(prev => {
            const next = new Set(prev);
            next.add(itemId);
            return next;
          });

          handleCartMutation(itemId, newQuantity)
            .then(() => {
              setUpdatingQuantities(prev => {
                const next = new Set(prev);
                next.delete(itemId);
                return next;
              });
            })
            .catch((err: Error) => {
              console.error('Error updating quantity:', err);

              setShopifyCart(previousCart);
              setUpdatingQuantities(prev => {
                const next = new Set(prev);
                next.delete(itemId);
                return next;
              });

              setError(err.message || 'Failed to update quantity');
              setTimeout(() => setError(null), 3000);
            });
        }}
        onRemoveItem={(itemId, _delta) => {
          if (!shopifyCart) return;

          // Find the item being removed
          const itemToRemove = shopifyCart.items.find(item => item.id === itemId);
          if (!itemToRemove) return;

          // Check if this is a main product (not an addon) with a Personalisation property
          const isAddon = itemToRemove.properties?.Addon === 'Yes' || itemToRemove.properties?.Addon === 'yes';
          const personalisationCode = itemToRemove.properties?.Personalisation;

          // If removing a main product with personalisation, find all addon items with the same personalisation code
          const addonItemsToRemove: number[] = [];
          if (!isAddon && personalisationCode) {
            shopifyCart.items.forEach(item => {
              const itemIsAddon = item.properties?.Addon === 'Yes' || item.properties?.Addon === 'yes';
              const itemPersonalisation = item.properties?.Personalisation;
              if (itemIsAddon && itemPersonalisation === personalisationCode && item.id !== itemId) {
                addonItemsToRemove.push(item.id);
              }
            });
          }

          // Store previous cart for rollback
          const previousCart = {
            ...shopifyCart,
            items: shopifyCart.items.map((item) => ({ ...item })),
          };

          // Optimistically remove item(s) from UI
          const itemsToRemove = [itemId, ...addonItemsToRemove];
          const updatedItems = shopifyCart.items.filter(item => !itemsToRemove.includes(item.id));
          const updatedTotalPrice = updatedItems.reduce((sum, item) => {
            return sum + (item.price * item.quantity);
          }, 0);
          const updatedItemCount = updatedItems.reduce((sum, item) => sum + item.quantity, 0);

          setShopifyCart({
            ...shopifyCart,
            items: updatedItems,
            total_price: updatedTotalPrice,
            item_count: updatedItemCount
          });

          // Mark all items as being removed
          setRemovingItems(prev => {
            const next = new Set(prev);
            itemsToRemove.forEach(id => next.add(id));
            return next;
          });

          // Remove all items (main product + addons) sequentially to avoid race conditions
          const removeItemsSequentially = async () => {
            try {
              for (const id of itemsToRemove) {
                await handleCartMutation(id, 0);
              }
              setRemovingItems(prev => {
                const next = new Set(prev);
                itemsToRemove.forEach(id => next.delete(id));
                return next;
              });
            } catch (err: any) {
              console.error('Error removing item(s):', err);

              setShopifyCart(previousCart);
              setRemovingItems(prev => {
                const next = new Set(prev);
                itemsToRemove.forEach(id => next.delete(id));
                return next;
              });

              setError(err.message || 'Failed to remove item(s)');
              setTimeout(() => setError(null), 3000);
            }
          };

          removeItemsSequentially();
        }}
      />

      {hasRedeemableProducts && (
        <>
          <div style={{ marginBottom: '12px' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 700,
              margin: '0 0 8px',
              color: '#222',
            }}>
              Crazy Deals!
            </h3>
          </div>

          <RedeemableProducts
            calculationResult={calculationResult}
            onRedeemableAdd={handleRedeemableAdd}
          />
        </>
      )}

      <DiscountCodeInput
        codes={discountCodes}
        onCodesChange={handleDiscountCodesChange}
        calculating={calculating}
        codeDiscounts={calculationResult?.code_discounts}
      />

      {error && (
        <div style={{
          padding: '12px',
          backgroundColor: '#fee',
          color: '#c33',
          borderRadius: '4px',
          marginBottom: '16px',
          fontSize: '14px'
        }}>
          Error: {error}
        </div>
      )}

      {/*{calculating && (
        <div style={{
          padding: '12px',
          textAlign: 'center',
          color: '#666',
          fontSize: '14px'
        }}>
          Calculating discounts...
        </div>
      )}*/}

      {calculationResult && (
        <DiscountSummary
          result={calculationResult}
          pointsEarned={projectedPointsEarned}
          effectiveMultiplier={effectiveMultiplier}
          isGuest={isGuest}
        />
      )}

      {!calculationResult && !calculating && !loading && shopifyCart && shopifyCart.items.length === 0 && (
        <div style={{
          padding: '12px',
          backgroundColor: '#fff3cd',
          color: '#856404',
          borderRadius: '4px',
          fontSize: '14px'
        }}>
          Your cart is empty. Add items to see discounts.
        </div>
      )}

      {/* Sticky Checkout Button */}
      <StickyCheckout
        shopifyCart={shopifyCart}
        calculationResult={calculationResult}
        calculating={calculating}
        creatingCheckout={creatingCheckout}
        onCheckout={handleCheckout}
      />

      {/* Add bottom padding to prevent content from being hidden behind sticky button */}
      <div
        className="intan-checkout-spacer"
        style={{
          height: '80px',
          minHeight: '80px'
        }}
      ></div>
      <style>{`
        @media (max-width: 768px) {
          .intan-checkout-spacer {
            height: 140px !important;
            min-height: 140px !important;
          }
        }
      `}</style>

      {/* Phone Verification Modal */}
      {customerId && (
        <PhoneVerificationModal
          isOpen={showPhoneModal}
          onClose={() => setShowPhoneModal(false)}
          onSuccess={async () => {
            // Refresh customer info after successful phone verification
            // Bypass cache to get fresh data with updated hasPhone status
            if (customerId) {
              try {
                const info = await fetchCustomerInfo(apiUrl, customerId, true); // bypassCache = true
                setHasPhone(info.hasPhone ?? true);
                setCustomerTags(info.tags || []);
                setLoyaltyInfo(info.loyalty || null);
              } catch (err) {
                console.error('Failed to refresh customer info:', err);
              }
            }
          }}
          apiUrl={apiUrl}
          customerId={customerId}
        />
      )}
    </div>
  );
};

